import { useEffect } from 'react'
import { useWidgetStore, type DemoActionType } from '@/stores/useWidgetStore'
import { useSessionStore } from '@/stores/useSessionStore'
import { WidgetChatSettings, WidgetSettings, WidgetBreakpointTab } from '@/types'
import { MenuType } from '@/types/widget'
import { logger } from '@/utils'

type DemoPostMessagePayload = {
  type: 'WIDGET_DEMO_UPDATE'
  widgetSettings?: WidgetSettings
  chatSettings?: WidgetChatSettings
  currentGlobalID?: string
  currentMenuType?: MenuType
  breakpoints?: {
    icon?: WidgetBreakpointTab
    welcomeMessage?: WidgetBreakpointTab
  }
  demoAction?: {
    type: DemoActionType
    data?: Record<string, unknown>
  }
}

const ALLOWED_MESSAGE_TYPE = 'WIDGET_DEMO_UPDATE'

/**
 * Hook that enables postMessage communication when the widget is in demo mode.
 * Listens for messages from the parent window containing widget settings updates.
 */
export function useDemoPostMessage() {
  const demo = useWidgetStore((state) => state.demo)

  useEffect(() => {
    if (!demo) return

    const handleMessage = (event: MessageEvent<DemoPostMessagePayload>) => {
      if (!event.data || typeof event.data !== 'object') return
      if (event.data.type !== ALLOWED_MESSAGE_TYPE) return

      logger.log('useDemoPostMessage', 'Received demo update', event.data)

      const { widgetSettings, chatSettings, currentGlobalID, currentMenuType, breakpoints, demoAction } =
        event.data

      if (widgetSettings) {
        useSessionStore.setState(
          { widgetSettings },
          false,
          'demoPostMessage/setWidgetSettings'
        )
      }

      // Allow the dashboard to toggle chat enablement / defaults without
      // sending the full widgetSettings payload.
      if (chatSettings) {
        useSessionStore.setState(
          (state) => ({
            widgetSettings: {
              ...(state.widgetSettings ?? {}),
              chatSettings: {
                ...(state.widgetSettings?.chatSettings ?? {}),
                ...chatSettings,
                screenSettings: {
                  ...(state.widgetSettings?.chatSettings?.screenSettings ?? {}),
                  ...(chatSettings.screenSettings ?? {}),
                },
              },
            },
          }),
          false,
          'demoPostMessage/setChatSettings'
        )
      }

      if (currentGlobalID) {
        useSessionStore.setState(
          { currentGlobalID },
          false,
          'demoPostMessage/setCurrentGlobalID'
        )
      }

      if (currentMenuType) {
        useSessionStore.setState(
          { currentMenuType },
          false,
          'demoPostMessage/setCurrentMenuType'
        )
      }

      if (breakpoints) {
        useWidgetStore.getState().setBreakpointOverrides({
          icon: breakpoints.icon ?? null,
          welcomeMessage: breakpoints.welcomeMessage ?? null,
        })
      }

      useWidgetStore.getState().setDemoAction(demoAction ?? { type: 'SHOW_CHAT' })
    }

    window.addEventListener('message', handleMessage)
    logger.log('useDemoPostMessage', 'Listener attached for demo mode')

    window.parent.postMessage({ type: 'WIDGET_DEMO_READY' }, '*')

    return () => {
      window.removeEventListener('message', handleMessage)
      logger.log('useDemoPostMessage', 'Listener removed')
    }
  }, [demo])
}
